import { useEffect, useMemo, useState } from 'react'
import { Container } from '@/components/layout/Container'
import { AccountSidebar } from '@/components/account/AccountSidebar'
import { useAuth } from '@/hooks/useAuth'
import { SignedInOnly } from '@/components/account/SignedInOnly'
import { updatePreferences } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Channel {
  key: string
  label: string
  description: string
}

const channels: Channel[] = [
  {
    key: 'order_updates',
    label: 'Order updates',
    description: 'Delivery status, tracking and returns for your orders.',
  },
  {
    key: 'new_drops',
    label: 'New drops',
    description: 'First access to new Little Sky launches.',
  },
  {
    key: 'promotions',
    label: 'Promotions and offers',
    description: 'Seasonal offers, sale previews and members-only discounts.',
  },
  {
    key: 'restock_alerts',
    label: 'Restock alerts',
    description: 'Let you know when a sold-out fragrance is back.',
  },
  {
    key: 'scent_quiz',
    label: 'Scent quiz follow-ups',
    description: 'Personalised scent recommendations based on your profile.',
  },
  {
    key: 'review_requests',
    label: 'Review reminders',
    description: 'Ask for feedback after an order arrives.',
  },
]

export function AccountPreferencesPage() {
  const { user, refreshUser } = useAuth()
  const [enabled, setEnabled] = useState<Record<string, boolean>>({})
  const [master, setMaster] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const preferences = useMemo(
    () => (user?.preferences ?? {}) as Record<string, unknown>,
    [user?.preferences]
  )

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  useEffect(() => {
    if (user) {
      setMaster(user.notificationOptIn)
      setEnabled(
        Object.fromEntries(
          channels.map((c) => [c.key, preferences[c.key] !== false])
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.notificationOptIn])

  const flipChannel = (key: string, value: boolean) => {
    setEnabled((e) => ({ ...e, [key]: value }))
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    const prefs = { ...preferences, ...enabled }
    const updated = await updatePreferences({ notificationOptIn: master, preferences: prefs })
    if (updated) {
      await refreshUser()
      setSaved(true)
    }
    setSaving(false)
  }

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <AccountSidebar activePath="/account/preferences" />

          <SignedInOnly>
          <div className="max-w-xl">
            <p className="mb-2 text-xs uppercase tracking-[0.2em] text-wd-orange">account</p>
            <h1 className="font-display text-4xl normal-case text-carbon md:text-5xl">
              Communication Preferences
            </h1>

            <section className="mt-10">
              <div className="mb-6 flex items-center justify-between border border-ink/10 p-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                    All communications
                  </p>
                  <p className="mt-1 text-sm text-carbon/60">
                    Master switch for every email we send.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={master}
                  aria-label="All communications"
                  onClick={() => {
                    setMaster((m) => !m)
                    setSaved(false)
                  }}
                  className={cn(
                    'relative h-6 w-11 shrink-0 border transition-colors',
                    master ? 'border-wd-orange bg-wd-orange' : 'border-ink/20 bg-ink/5'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 h-4.5 w-4.5 bg-white transition-all',
                      master ? 'left-[22px]' : 'left-0.5'
                    )}
                    style={{ width: 18, left: master ? 22 : 2, height: 18, top: 2 }}
                  />
                </button>
              </div>

              <ul className="divide-y divide-ink/10 border-y border-ink/10">
                {channels.map((c) => {
                  const on = !!enabled[c.key]
                  return (
                    <li key={c.key} className="flex items-center justify-between gap-6 py-5">
                      <div>
                        <p className="text-sm font-medium text-carbon">{c.label}</p>
                        <p className="mt-0.5 text-sm text-carbon/60">{c.description}</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={on}
                        aria-label={c.label}
                        onClick={() => flipChannel(c.key, !on)}
                        className={cn(
                          'relative h-6 w-11 shrink-0 border transition-colors',
                          on ? 'border-wd-orange bg-wd-orange' : 'border-ink/20 bg-ink/5'
                        )}
                      >
                        <span
                          className="absolute bg-white transition-all"
                          style={{ width: 18, height: 18, top: 2, left: on ? 22 : 2 }}
                        />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>

            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 bg-ink px-6 py-3 text-xs font-medium tracking-[0.12em] text-white transition-all duration-300 hover:bg-wd-orange disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save preferences'}
              </button>
              {saved && <span className="text-sm text-carbon/60">Preferences updated.</span>}
            </div>
          </div>
          </SignedInOnly>
        </div>
      </Container>
    </div>
  )
}